import { REQUEST_METHODS } from '@/constants/network'
import baseFetch from '@/network/baseFetch'

export interface VisaDenialPayload {
    travelCategory: string
    travelContext: {
        demandContext: {
            partnerId: string
            platformType: string
            sellingCountry?: string
            userCohort: string
        }
    }
    tripDetail: {
        journey: {
            departureAirport: string
            arrivalAirport: string
            countryCode: string
            departDate: string
            returnDate?: string
        }
        sectorType: string
        tripType: string
    }
}

export interface VisaDenialResponse {
    status?: number
    success?: boolean
    error?: any
    body?: {
        programList: Array<{
            name: string
            valueAddedServices?: Array<{
                type: string
                insuranceType: string
            }>
        }>
        travelCategory?: string
    }
    // Support legacy structure where body is nested under data
    data?: {
        body: {
            programList: Array<{
                name: string
                valueAddedServices?: Array<{
                    type: string
                    insuranceType: string
                }>
            }>
            travelCategory?: string
        }
    }
}

/**
 * API POST Helper for visa denial requests
 * Wraps baseFetch with proper headers for POST requests
 */
const apiPostHelper = async <T = unknown>(
    url: string,
    payload: object,
    contentType: string = 'application/json',
    headers: Record<string, string> = {}
): Promise<T> => {
    const response = await baseFetch<T>({
        url,
        method: REQUEST_METHODS.POST,
        headers: {
            'Content-Type': contentType,
            ...headers
        },
        body: JSON.stringify(payload)
    })
    return response
}

/**
 * Check if VISA_DENIAL feature flag is enabled
 * For now, defaulting to true. This should be replaced with actual feature flag check
 */
export const isVisaDenialFeatureEnabled = (): boolean => {
    // TODO: Replace with actual feature flag check from buildVariant/featureConfig
    // Example: return featureConfig[buildVariant]?.VISA_DENIAL === true
    return true
}

/**
 * Visa Denial Eligibility API call
 * Checks if Visa Denial Insurance is available for a given flight search
 */
export const fetchVisaDenialEligibility = async (
    payload: VisaDenialPayload
): Promise<VisaDenialResponse | {}> => {
    if (!isVisaDenialFeatureEnabled()) {
        return {}
    }

    try {
        const URL = `/flight/home/vasDiscovery`
        const header = {
            product: 'air',
            channel: 'pwa' // Desktop PWA - lowercase as per API requirement
        }
        const response = await apiPostHelper<VisaDenialResponse>(
            URL,
            payload,
            'application/json',
            header
        )
        return response || {}
    } catch (e) {
        console.error('Visa denial eligibility API error:', e)
        return {}
    }
}

/**
 * Parse visa denial response to check if visa denial is available
 * Checks if body.programList contains a program with name === 'VISA_DENIAL_INSURANCE'
 */
export const isVisaDenialAvailable = (responseData: VisaDenialResponse | VisaDenialResponse['data']): boolean => {
    // Handle both response structures:
    // 1. Direct response with body at top level: { body: { programList: [...] } }
    // 2. Nested response: { data: { body: { programList: [...] } } }
    let programList: Array<{ name: string }> | undefined

    if ('body' in responseData && responseData.body?.programList) {
        // Direct structure: response.body.programList
        programList = responseData.body.programList
    } else if ('data' in responseData && responseData.data?.body?.programList) {
        // Nested structure: response.data.body.programList
        programList = responseData.data.body.programList
    } else if (responseData && typeof responseData === 'object' && 'body' in responseData) {
        // Fallback: try to access body directly
        const body = (responseData as any).body
        if (body?.programList) {
            programList = body.programList
        }
    }

    if (!programList || !Array.isArray(programList)) {
        return false
    }

    // Check if any program in programList has name === 'VISA_DENIAL_INSURANCE'
    const isAvailable = programList.some(
        program => program.name === 'VISA_DENIAL_INSURANCE'
    )

    return isAvailable
}

